'use client';

import { theme } from '@/lib/theme';
import { MantineProvider } from '@mantine/core';
import { QueryClientProvider } from '@tanstack/react-query';
import { SessionProvider, useSession } from 'next-auth/react';
import { PropsWithChildren, useEffect } from 'react';
import { setRollbarUser } from '@/lib/error-tracking';
import { PermissionProvider } from './permissions';
import { PostHogProvider } from './posthog';
import styles from './styles.module.css';
import { getQueryClient } from './tanstack-query-provider';

function TanstackQueryProvider({ children }: { children: React.ReactNode }) {
  const queryClient = getQueryClient();
  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
}

function FeaturesPermissionProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { data: session } = useSession();
  return (
    <PermissionProvider permissions={session?.user.accessibleFeatureKeys || []}>
      {children}
    </PermissionProvider>
  );
}

function RollbarSessionSync() {
  const { data: session, status } = useSession();

  useEffect(() => {
    if (status !== 'authenticated' || !session?.user) {
      setRollbarUser(null);
      return;
    }

    const composedName = [session.user.firstName, session.user.lastName]
      .filter(Boolean)
      .join(' ')
      .trim();

    setRollbarUser({
      id: session.user._id,
      email: session.user.email || undefined,
      name: session.user.name || composedName || undefined,
    });
  }, [session, status]);

  return null;
}

// Create an inner component that uses session
function ProvidersInner({ children }: PropsWithChildren) {
  return (
    <TanstackQueryProvider>
      <MantineProvider
        theme={{
          ...theme,
          activeClassName: styles.active,
          components: {
            TextInput: {
              classNames: {
                input:
                  'focus:border-[#0CAE5C] focus:ring-0 border-gray-300 placeholder:text-gray-400 placeholder:italic',
              },
            },
          },
        }}
      >
        <PostHogProvider>
          <RollbarSessionSync />
          <FeaturesPermissionProvider>{children}</FeaturesPermissionProvider>
        </PostHogProvider>
      </MantineProvider>
    </TanstackQueryProvider>
  );
}

const Providers = ({ children }: PropsWithChildren) => {
  return (
    <SessionProvider>
      <ProvidersInner>{children}</ProvidersInner>
    </SessionProvider>
  );
};

export default Providers;
